


















































import Vue from "vue";
import Page from "@/components/Page.vue";

import { ApiSearchColumn, ColumnType } from "@/components/apiSearch/models/ApiSearch";

import InterfaceViewToolbar from "../components/InterfaceViewToolbar.vue";
import SyncEntityList from "../components/SyncEntityList.vue";
import store, { InterfaceStore } from "../services/InterfaceStore";
import { IfaceType } from "../models/IfaceType";


interface Data {
  store: InterfaceStore;
  IfaceType: typeof IfaceType;
}

interface Methods {
  getUrl(onlyErrors: boolean): string;
  getColumns(onlyErrors: boolean): ApiSearchColumn[];
}

interface Computed {
  syncListTitle: string;
  syncErrorListTitle: string;
  /** API search URL a hibás szinkron tételekhez (típus + interfész alapján) */
  errorListUrl: string;
  /** API search URL a (nem hibás) szinkron tételekhez */
  syncListUrl: string;
  /** Oszlopok a hibás szinkron tételekhez (típus alapján) */
  errorListColumns: ApiSearchColumn[];
  /** Oszlopok a (nem hibás) szinkron tételekhez (típus alapján) */
  syncListColumns: ApiSearchColumn[];
  /** Toolbarból érkező globális szűrő — apiSearchExtraFilter prop tartalma */
  syncListExtraFilter: object;
  errorListExtraFilter: object;
}

interface Props {
  /**
   * Előzmény mód (Előzmények menüpont) — a listákon csak megtekintő funkciók érhetők el,
   * a toolbaron pedig csak a szűrők.
   */
  historyMode: boolean;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    Page,
    InterfaceViewToolbar,
    SyncEntityList,
  },

  props: {
    historyMode: { type: Boolean, default: false },
  },

  data() {
    return {
      store,
      IfaceType,
    };
  },

  computed: {
    syncListTitle() {
      let title = "Szinkron tételek";
      switch (this.store.selectedInterfaceType) {
        case IfaceType.Partner:     title = "Sikeresen átszinkronizált partnerek"; break;
        case IfaceType.Part:        title = "Sikeresen átszinkronizált cikkek"; break;
        case IfaceType.Car:         title = "Sikeresen átszinkronizált gépjárművek"; break;
        case IfaceType.InInvoice:   title = "Sikeresen átszinkronizált bejövő számlák"; break;
        case IfaceType.OutInvoice:  title = "Sikeresen átszinkronizált kimenő számlák";
      }
      return title;
    },
    syncErrorListTitle() {
      let title = "Hibás szinkron tételek";
      switch (this.store.selectedInterfaceType) {
        case IfaceType.Partner:     title = "Sikertelenül átszinkronizált partnerek"; break;
        case IfaceType.Part:        title = "Sikertelenül átszinkronizált cikkek"; break;
        case IfaceType.Car:         title = "Sikertelenül átszinkronizált gépjárművek"; break;
        case IfaceType.InInvoice:   title = "Sikertelenül átszinkronizált bejövő számlák"; break;
        case IfaceType.OutInvoice:  title = "Sikertelenül átszinkronizált kimenő számlák";
      }
      return title;
    },
    errorListUrl(): string {
      return this.getUrl(true);
    },

    syncListUrl(): string {
      return this.getUrl(false);
    },

    errorListColumns(): any[] {
      return this.getColumns(true);
    },

    syncListColumns(): any[] {
      return this.getColumns(false);
    },

    /** Sikeres listára érvényes globális szűrő (a 3 nem-hiba checkbox alapján). */
    syncListExtraFilter(): object {
      const selectedStatuses: number[] = [];
      if (this.store.statusInProgressOn) selectedStatuses.push(2);
      if (this.store.statusDoneOn) selectedStatuses.push(3);
      if (this.store.statusSkipOn) selectedStatuses.push(5);
      return {
        globalSearch: this.store.globalSearchText,
        fromDate: this.store.fromDate,
        toDate: this.store.toDate,
        selectedStatuses,
      };
    },

    /** Hibás listára érvényes globális szűrő (státusz nélkül — ott csak Error van). */
    errorListExtraFilter(): object {
      return {
        globalSearch: this.store.globalSearchText,
        fromDate: this.store.fromDate,
        toDate: this.store.toDate,
        selectedStatuses: null,
      };
    },
  },

  methods: {
    getColumns(onlyErrors) {
      const errorStatusId = 4;

      const commandCol = {
        field: "selected",
        title: "",
        cell: "command",
        filterable: false,
        sortable: false,
        checkBox: true,
      };

      const statusCol = {
        field: "syncStatusName",
        title: "Státusz",
        width: 140,
        cell: "syncStatusName",
        multiselect: true,
        multiselectField: "selectedSyncStatuses",
        multiselectOptions: this.store.syncStatuses
          .filter((s) => s.id !== errorStatusId)
          .map((s) => ({ text: s.name, value: s.id })),
        visible: !onlyErrors,
      };

      switch (this.store.selectedInterfaceType) {
        case IfaceType.Partner: return [
          { ...commandCol, width: 165 },
          { field: "extCode", title: "Külső azonosító", width: 100, cell: "disabledText" },
          { field: "partner", title: "Belső azonosító", width: 100, type: ColumnType.Right, cell: "disabledText" },
          { field: "name", title: "Név", cell: "name" },
          { field: "taxaccount", title: "Adószám", width: 110, cell: "editableText" },
          { field: "euTaxaccount", title: "EU adószám", width: 110, cell: "editableText" },
          { field: "countryCode", title: "Ország", width: 60, cell: "editableText" },
          { field: "postcode", title: "Irányítószám", width: 90, type: ColumnType.Right, cell: "editableText" },
          { field: "city", title: "Város", width: 130, cell: "editableText" },
          { field: "address", title: "Cím", cell: "editableText" },
          { field: "syncRemark", title: "Megjegyzés", width: 200, cell: "editableText" },
          { field: "errorText", title: "Hibaüzenet", width: 220, cell: "disabledText", fieldClass: "error-text-col", visible: onlyErrors },
          statusCol,
          { field: "updUserName", title: "Utoljára módosította", width: 150, cell: "disabledText" },
          { field: "updDateTime", title: "Utolsó mod. ideje", width: 150, range: true, cell: "updDateTime" },
        ];
        case IfaceType.InInvoice:
        case IfaceType.OutInvoice: {
          return [
            { ...commandCol, width: 205 },
            { field: "extCode", title: "Külső azonosító", width: 120, cell: "disabledText" },
            { field: "invNo", title: "Számlaszám", width: 150, cell: "invNo" },
            { field: "partnerCode", title: "Partner", width: 230, cell: "invoicePartner" },
            { field: "invDate", title: "Számla dátuma", width: 150, cell: "editableDate", range: true },
            { field: "shipDate", title: "Telj. dátuma", width: 150, cell: "editableDate", range: true },
            { field: "pterm", title: "Fizetési hat.idő", width: 150, cell: "editableDate", range: true },
            { field: "currNetto", title: "Nettó (dev.)", width: 110, type: ColumnType.Money, cell: "calculatedMoney", filterable: false },
            { field: "currVat", title: "Áfa (dev.)", width: 110, type: ColumnType.Money, cell: "calculatedMoney", filterable: false },
            { field: "currTotal", title: "Bruttó (dev.)", width: 110, type: ColumnType.Money, cell: "editableMoney", filterable: false },
            { field: "invCurrency", title: "Pénznem", width: 60, cell: "disabledText" },
            { field: "exchangeRate", title: "Árfolyam", width: 60, type: ColumnType.Money, cell: "disabledText" },
            { field: "payMode", title: "Fizetési mód", width: 170, cell: "payMode" },
            { field: "syncRemark", title: "Megjegyzés", width: 150, cell: "editableText" },
            { field: "errorText", title: "Hibaüzenet", width: 200, cell: "disabledText", fieldClass: "error-text-col", visible: onlyErrors },
            statusCol,
            { field: "updUserName", title: "Utoljára módosította", width: 150, cell: "disabledText" },
            { field: "updDateTime", title: "Utolsó mod. ideje", width: 150, range: true, cell: "updDateTime" },
          ];
        }
        default:
          return [commandCol];
      }
    },

    getUrl(onlyErrors) {
      if (this.store.selectedInterface <= 1 || this.store.selectedInterfaceType <= 1)
        return "";

      switch (this.store.selectedInterfaceType) {
        case IfaceType.Partner:    return `iface-sync-partner/search/${this.store.selectedInterface}/${onlyErrors}`;
        case IfaceType.InInvoice:  return `iface-sync-invoice-head/search/${this.store.selectedInterface}/IN/${onlyErrors}`;
        case IfaceType.OutInvoice: return `iface-sync-invoice-head/search/${this.store.selectedInterface}/OUT/${onlyErrors}`;
        default: return "";
      }
    },
  },
});
